import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import './FeedbackForm.css';


const API_URL = process.env.REACT_APP_API_URL || 'https://verdant-production-v2-production.up.railway.app/api';

const TOTAL_STEPS = 6;

const getRatingLabels = (type) => {
  const labels = {
    enjoyment: [
      "Rather Terrible",
      "Not for me",
      "It's ok",
      "Enjoyed it",
      "Really liked it",
      "Loved it",
      "I would like to order now please",
    ],
    sweetness: [
      "Way too bitter",
      "Not sweet enough",
      "Could be a touch sweeter",
      "Just right",
      "A touch sweet",
      "Too sweet",
      "Way too sweet",
    ],
    acidity: [
      "Flat — no brightness",
      "Could use more zip",
      "A touch more would help",
      "Just right",
      "Slightly tart",
      "Too sharp",
      "Way too acidic",
    ],
    complexity: [
      "Bland",
      "A bit one note",
      "Could be more layered",
      "Just right",
      "A lot going on",
      "Complex — hard to follow",
      "Overwhelming",
    ],
    ingredient: [
      "Couldn't taste it",
      "Barely there",
      "Could use more",
      "Just right",
      "Noticeable",
      "A bit much",
      "Way too dominant",
    ],
  };
  return labels[type] || labels.ingredient;
};

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const RatingScale = ({ type, value, onChange, name }) => {
  const labels = getRatingLabels(type);
  return (
    <div className="vf-radio-scale">
      {labels.map((label, i) => {
        const optionValue = i + 1;
        const isSelected = value === optionValue;
        return (
          <label key={optionValue} className={`vf-radio-option${isSelected ? ' selected' : ''}`}>
            <input
              type="radio"
              name={name}
              value={optionValue}
              checked={isSelected}
              onChange={() => onChange(optionValue)}
            />
            <span className="vf-radio-label">{label}</span>
          </label>
        );
      })}
    </div>
  );
};

const FeedbackForm = () => {
  const { batchNumber } = useParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const distributionToken = searchParams.get('t') || null;
  const [showPostSubmit, setShowPostSubmit] = useState(false);
  const [postSubmitSent, setPostSubmitSent] = useState(false);
  const [postSubmitData, setPostSubmitData] = useState({
    wouldBuyResponse: '',
    remindedOf: '',
    wouldGiveTo: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [batchData, setBatchData] = useState(null);
  const [userFingerprint, setUserFingerprint] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    batch_id: null,
    batch_number: '',
    respondent_name: '',
    respondent_email: '',
    user_fingerprint: '',
    sweetness_rating: null,
    acidity_rating: null,
    complexity_rating: null,
    balance_rating: null,
    overall_enjoyment: null,
    ingredient_ratings: {},
    taste_descriptors: [],
    aroma_descriptors: [],
    appearance_descriptors: [],
    flavors_want_more: [],
    flavors_too_much: [],
    shrub_ratio: null,
    mix_type: null,
    serving_method: null,
    used_in_cocktail: false,
    cocktail_spirits: [],
    food_pairing_interest: false,
    taste_notes: '',
    aroma_notes: '',
    worst_aspect: '',
    additional_thoughts: '',
  });

  useEffect(() => {
    const initFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setUserFingerprint(result.visitorId);
        setFormData(prev => ({ ...prev, user_fingerprint: result.visitorId }));
      } catch (err) {
        console.error('Failed to initialize fingerprint:', err);
      }
    };
    initFingerprint();
  }, []);

  useEffect(() => {
    const loadBatchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/feedback/form/${batchNumber}`);
        if (!response.data.allowed) {
          setError('This batch is not currently accepting feedback.');
          setLoading(false);
          return;
        }
        setBatchData(response.data);
        const ingredientRatings = {};
        response.data.ingredients.forEach(ing => {
          ingredientRatings[ing.name] = null;
        });
        setFormData(prev => ({
          ...prev,
          batch_id: response.data.batch.id,
          batch_number: response.data.batch.final_lot_number,
          ingredient_ratings: ingredientRatings,
        }));
        setLoading(false);
      } catch (err) {
        setError('Failed to load batch information.');
        setLoading(false);
      }
    };
    loadBatchData();
  }, [batchNumber]);

  // Populate the App header slot with the drink name once data loads
  useEffect(() => {
    const slot = document.getElementById('vf-header-slot');
    if (!slot) return;
    if (batchData?.batch?.sales_name) {
      slot.textContent = batchData.batch.sales_name;
    }
    return () => { slot.textContent = ''; };
  }, [batchData]);

  const handleRatingChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: parseInt(value) }));
  };

  const handleIngredientRatingChange = (ingredient, value) => {
    setFormData(prev => ({
      ...prev,
      ingredient_ratings: {
        ...prev.ingredient_ratings,
        [ingredient]: parseInt(value),
      },
    }));
  };

  const handleToggleArray = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter(i => i !== value)
          : [...current, value],
      };
    });
  };

  const handleSingleSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: prev[field] === value ? null : value }));
  };

  const goNext = () => {
    setCurrentStep(s => Math.min(s + 1, TOTAL_STEPS));
    window.scrollTo(0, 0);
  };

  const goBack = () => {
    setCurrentStep(s => Math.max(s - 1, 0));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (formData.respondent_email) {
        localStorage.setItem('verdant_email', formData.respondent_email);
      }
      const response = await axios.post(`${API_URL}/feedback`, {
        ...formData,
        distributionTokenCode: distributionToken,
        fingerprint: userFingerprint,
      });
      setSubmitting(false);
      if (distributionToken) {
        localStorage.setItem(`vd_submitted_${distributionToken}`, Date.now().toString());
        setSubmitted(true);
        setTimeout(() => setShowPostSubmit(true), 1800);
      } else {
        navigate(`/feedback/thankyou/${batchNumber}`, {
          state: {
            userRating: formData.overall_enjoyment,
            responseId: response.data.id,
            previousCount: response.data.previousResponseCount,
          },
        });
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handlePostSubmit = async () => {
    try {
      await axios.patch(`${API_URL}/feedback/post-submit`, {
        distributionTokenCode: distributionToken,
        fingerprint: userFingerprint,
        ...postSubmitData,
      });
    } catch (err) {
      console.error('Post-submit error:', err);
    } finally {
      setPostSubmitSent(true);
      setShowPostSubmit(false);
    }
  };

  if (submitted) {
    return (
      <div className="vf-container">
        {!showPostSubmit && !postSubmitSent && (
          <div style={{ padding: '64px 24px', textAlign: 'center' }}>
            <p style={{
              fontFamily: "'Cormorant', serif",
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: '18px',
              color: '#9CA38C',
            }}>
              Thank you.
            </p>
          </div>
        )}

        {showPostSubmit && (
          <div style={{
            padding: '48px 24px',
            maxWidth: '480px',
            margin: '0 auto',
            fontFamily: "'Archivo', sans-serif",
            fontWeight: 300,
          }}>
            <p style={{
              fontFamily: "'Cormorant', serif",
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: '15px',
              color: '#9CA38C',
              letterSpacing: '0.04em',
              marginBottom: '32px',
            }}>
              Officially done. But if you have one more minute —
            </p>

            {[
              { key: 'wouldBuyResponse', label: 'If this was $18 at a market, would you buy it?', placeholder: 'Yes, no, maybe — and why.' },
              { key: 'remindedOf', label: 'What did it remind you of?', placeholder: 'A place, a feeling, a meal — anything.' },
              { key: 'wouldGiveTo', label: 'Who would you give this to?', placeholder: "Don't overthink it." },
            ].map(({ key, label, placeholder }) => (
              <div key={key} style={{ marginBottom: '28px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  color: '#3A4A3D',
                  marginBottom: '10px',
                  fontFamily: "'Cormorant', serif",
                  fontWeight: 300,
                }}>
                  {label}
                </label>
                <textarea
                  rows={2}
                  placeholder={placeholder}
                  value={postSubmitData[key]}
                  onChange={e => setPostSubmitData(p => ({ ...p, [key]: e.target.value }))}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: '0.5px solid #9CA38C66',
                    borderRadius: 0,
                    padding: '12px',
                    fontFamily: "'Archivo', sans-serif",
                    fontWeight: 300,
                    fontSize: '14px',
                    color: '#2C2C2C',
                    resize: 'none',
                    outline: 'none',
                  }}
                />
              </div>
            ))}

            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <button
                onClick={handlePostSubmit}
                style={{
                  background: '#3A4A3D',
                  border: 'none',
                  color: '#F8F6F0',
                  fontFamily: "'Archivo', sans-serif",
                  fontWeight: 300,
                  fontSize: '12px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  padding: '12px 28px',
                  cursor: 'pointer',
                }}
              >
                Send it
              </button>
              <button
                onClick={() => setShowPostSubmit(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9CA38C',
                  fontFamily: "'Archivo', sans-serif",
                  fontWeight: 200,
                  fontSize: '12px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                I'm done, thanks
              </button>
            </div>
          </div>
        )}

        {postSubmitSent && (
          <p style={{
            fontFamily: "'Cormorant', serif",
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: '16px',
            color: '#9CA38C',
            textAlign: 'center',
            padding: '32px 24px',
          }}>
            Appreciated.
          </p>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="vf-container">
        <div className="vf-loading">
          <p>One moment...</p>
        </div>
      </div>
    );
  }

  if (error && !batchData) {
    return (
      <div className="vf-container">
        <div className="vf-error">{error}</div>
      </div>
    );
  }

  const ingredients = batchData?.ingredients || [];

  // Vinegar may be a blend-stage component absent from the mash batch query;
  // add it as a fallback so it is always rateable
  const hasVinegar = ingredients.some(
    ing => ing.name === 'Vinegar' || ing.type === 'Vinegar'
  );
  const ingredientsWithVinegar = hasVinegar
    ? ingredients
    : [...ingredients, { id: 'vinegar', name: 'Vinegar', type: 'Vinegar' }];

  const displayIngredients = ingredientsWithVinegar.filter(
    ing => !ing.name.toLowerCase().includes('sugar') && !ing.name.toLowerCase().includes('turbinado')
  );

  const steps = [
    <div className="vf-step" key="enjoy">
      <div className="vf-step-label">Overall</div>
      <div className="vf-step-q">How much did you enjoy it?</div>
      <RatingScale
        type="enjoyment"
        value={formData.overall_enjoyment}
        onChange={(val) => handleRatingChange('overall_enjoyment', val)}
        name="rating-overall"
      />
    </div>,

    <div className="vf-step" key="balance">
      <div className="vf-step-label">Balance</div>
      <div className="vf-step-q">How did it feel in the glass?</div>
      {[
        { field: 'sweetness_rating', label: 'Sweetness', type: 'sweetness' },
        { field: 'acidity_rating', label: 'Acidity', type: 'acidity' },
        { field: 'complexity_rating', label: 'Complexity', type: 'complexity' },
      ].map(({ field, label, type }) => (
        <div key={field} className="vf-rating-section">
          <div className="vf-rating-section-label">{label}</div>
          <RatingScale
            type={type}
            value={formData[field]}
            onChange={(val) => handleRatingChange(field, val)}
            name={`rating-${field}`}
          />
        </div>
      ))}
    </div>,

    <div className="vf-step" key="flavours">
      <div className="vf-step-label">Flavours</div>
      <div className="vf-step-q">What did you taste?</div>
      {displayIngredients.length > 0 && (
        <>
          <div className="vf-chip-section">
            <div className="vf-chip-label">Want more of</div>
            <div className="vf-chip-group">
              {displayIngredients.map(ing => (
                <button
                  key={`more-${ing.id}`}
                  className={`vf-chip${formData.flavors_want_more.includes(ing.name) ? ' selected' : ''}`}
                  onClick={() => handleToggleArray('flavors_want_more', ing.name)}
                  type="button"
                >{capitalize(ing.name)}</button>
              ))}
            </div>
          </div>
          <div className="vf-chip-section">
            <div className="vf-chip-label">Too strong</div>
            <div className="vf-chip-group">
              {displayIngredients.map(ing => (
                <button
                  key={`less-${ing.id}`}
                  className={`vf-chip${formData.flavors_too_much.includes(ing.name) ? ' selected' : ''}`}
                  onClick={() => handleToggleArray('flavors_too_much', ing.name)}
                  type="button"
                >{capitalize(ing.name)}</button>
              ))}
            </div>
          </div>
          <div className="vf-chip-section">
            <div className="vf-chip-label">Ingredient balance (optional)</div>
            {displayIngredients.map(ing => (
              <div key={`rate-${ing.id}`} className="vf-rating-section">
                <div className="vf-rating-section-label vf-italic">{capitalize(ing.name)}</div>
                <RatingScale
                  type="ingredient"
                  value={formData.ingredient_ratings[ing.name]}
                  onChange={(val) => handleIngredientRatingChange(ing.name, val)}
                  name={`ingredient-${ing.name}`}
                />
              </div>
            ))}
          </div>
        </>
      )}
      <div className="vf-chip-section">
        <div className="vf-chip-label">Taste descriptors</div>
        <div className="vf-chip-group">
          {['Bright', 'Tangy', 'Sweet', 'Herbal', 'Funky', 'Sharp', 'Flat', 'Bitter', 'Floral', 'Earthy', 'Tart', 'Warming'].map(d => (
            <button
              key={d}
              className={`vf-chip${formData.taste_descriptors.includes(d) ? ' selected' : ''}`}
              onClick={() => handleToggleArray('taste_descriptors', d)}
              type="button"
            >{d}</button>
          ))}
        </div>
      </div>
    </div>,

    <div className="vf-step" key="serve">
      <div className="vf-step-label">How you made it</div>
      <div className="vf-step-q">How did you drink it?</div>
      <div className="vf-serve-grid">
        {[
          { label: 'Sparkling water', value: 'Soda Water', icon: '🫧' },
          { label: 'Still water', value: 'Still Water', icon: '💧' },
          { label: 'In a cocktail', value: 'In a cocktail', icon: '🍸' },
          { label: 'Straight / ice', value: 'Straight', icon: '🧊' },
        ].map(({ label, value, icon }) => (
          <button
            key={value}
            className={`vf-serve-tile${formData.mix_type === value ? ' selected' : ''}`}
            onClick={() => setFormData(prev => ({
              ...prev,
              mix_type: prev.mix_type === value ? null : value,
              serving_method: prev.mix_type === value ? null : label,
              used_in_cocktail: value === 'In a cocktail' && prev.mix_type !== value,
            }))}
            type="button"
          >
            <span className="vf-serve-icon">{icon}</span>
            {label}
          </button>
        ))}
      </div>
      {formData.mix_type === 'In a cocktail' && (
        <div className="vf-chip-section">
          <div className="vf-chip-label">Spirits used</div>
          <div className="vf-chip-group">
            {['Tequila', 'Gin', 'Vodka', 'Rum', 'Whiskey', 'Other'].map(s => (
              <button
                key={s}
                className={`vf-chip${formData.cocktail_spirits.includes(s) ? ' selected' : ''}`}
                onClick={() => handleToggleArray('cocktail_spirits', s)}
                type="button"
              >{s}</button>
            ))}
          </div>
        </div>
      )}
      <div className="vf-chip-section">
        <div className="vf-chip-label">Ratio (approx)</div>
        <div className="vf-chip-group">
          {['1:3', '1:4', '1:5', '1:6+'].map(r => (
            <button
              key={r}
              className={`vf-chip${formData.shrub_ratio === r ? ' selected' : ''}`}
              onClick={() => handleSingleSelect('shrub_ratio', r)}
              type="button"
            >{r}</button>
          ))}
        </div>
      </div>
    </div>,

    <div className="vf-step" key="notes">
      <div className="vf-step-label">Your words</div>
      <div className="vf-step-q">Anything else we should know?</div>
      <textarea
        className="vf-textarea"
        rows="4"
        placeholder="Tasting notes, first impressions..."
        value={formData.taste_notes}
        onChange={e => setFormData(prev => ({ ...prev, taste_notes: e.target.value }))}
      />
      <textarea
        className="vf-textarea"
        rows="3"
        placeholder="What would you change? (optional)"
        value={formData.worst_aspect}
        onChange={e => setFormData(prev => ({ ...prev, worst_aspect: e.target.value }))}
      />
      <textarea
        className="vf-textarea"
        rows="3"
        placeholder="Anything else? (optional)"
        value={formData.additional_thoughts}
        onChange={e => setFormData(prev => ({ ...prev, additional_thoughts: e.target.value }))}
      />
    </div>,

    <div className="vf-step" key="contact">
      <div className="vf-step-label">Optional</div>
      <div className="vf-step-q">Want to stay in the loop?</div>
      <input
        className="vf-text-input"
        type="text"
        placeholder="Name"
        value={formData.respondent_name}
        onChange={e => setFormData(prev => ({ ...prev, respondent_name: e.target.value }))}
      />
      <input
        className="vf-text-input"
        type="email"
        placeholder="Email"
        value={formData.respondent_email}
        onChange={e => setFormData(prev => ({ ...prev, respondent_email: e.target.value }))}
      />
      {error && <div className="vf-inline-error">{error}</div>}
    </div>,
  ];

  return (
    <div className="vf-container">
      <p className="vf-step-intro">Thank you for tasting and taking a moment to share your thoughts.</p>
      <div className="vf-progress">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div key={i} className={`vf-dot${i <= currentStep ? ' done' : ''}`} />
        ))}
      </div>

      {steps[currentStep]}

      <div className="vf-actions">
        {currentStep < TOTAL_STEPS - 1 && (
          <>
            <button className="vf-btn-primary" onClick={goNext} type="button">Next</button>
            {currentStep > 0 && (
              <button className="vf-skip" onClick={goNext} type="button">skip</button>
            )}
          </>
        )}
        {currentStep === TOTAL_STEPS - 1 && (
          <>
            <button className="vf-btn-primary" onClick={handleSubmit} disabled={submitting} type="button">
              {submitting ? 'Sending...' : 'Submit'}
            </button>
            <button className="vf-btn-secondary" onClick={handleSubmit} disabled={submitting} type="button">
              Submit anonymously
            </button>
          </>
        )}
        {currentStep > 0 && (
          <button className="vf-back" onClick={goBack} type="button">← back</button>
        )}
      </div>
    </div>
  );
};

export default FeedbackForm;
